<template>
  <div class="walmart-people-flow">
    <div class="walmart-people-flow__header">
      <div>
        <h5 class="mb-0">{{ $t('grid.widgets_section.walmart_people_flow.title') }}</h5>
        <small class="text-muted">{{ chartSubtitle }}</small>
      </div>
      <feather-icon
        icon="RefreshCwIcon"
        class="cursor-pointer"
        :class="{ 'walmart-people-flow__refreshing': loading }"
        size="18"
        @click="refreshSelectedView"
      />
    </div>

    <div class="walmart-people-flow__filters">
      <v-select
        v-model="viewMode"
        :options="viewModeOptions"
        :clearable="false"
        :reduce="(option) => option.value"
        @input="onViewModeChange"
      />
      <v-select
        v-if="viewMode === 'history'"
        v-model="historicalRange"
        :options="historicalRangeOptions"
        :clearable="false"
        :reduce="(option) => option.value"
        @input="onHistoricalRangeChange"
      />
    </div>

    <div class="walmart-people-flow__zoom">
      <small class="text-muted">
        {{ $t('grid.widgets_section.walmart_people_flow.interval_minutes', { minutes: zoomInterval }) }}
      </small>
      <div class="walmart-people-flow__zoom-actions">
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="!canZoomOut || loading"
          :title="$t('grid.widgets_section.walmart_people_flow.zoom_out')"
          @click="zoomOut"
        >
          <feather-icon icon="ZoomOutIcon" size="16" />
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="!canZoomIn || loading"
          :title="$t('grid.widgets_section.walmart_people_flow.zoom_in')"
          @click="zoomIn"
        >
          <feather-icon icon="ZoomInIcon" size="16" />
        </b-button>
      </div>
    </div>

    <div v-if="viewMode === 'history' && historicalRange === 'custom'" class="walmart-people-flow__custom-range">
      <flat-pickr
        v-model="customFrom"
        class="form-control"
        :config="customDateConfig"
        :placeholder="$t('grid.widgets_section.walmart_people_flow.from')"
      />
      <flat-pickr
        v-model="customTo"
        class="form-control"
        :config="customDateConfig"
        :placeholder="$t('grid.widgets_section.walmart_people_flow.to')"
      />
      <b-button
        variant="primary"
        size="sm"
        :disabled="!customRangeIsValid || loading"
        @click="applyCustomRange"
      >
        {{ $t('grid.widgets_section.walmart_people_flow.apply') }}
      </b-button>
    </div>
    <small
      v-if="viewMode === 'history' && historicalRange === 'custom' && !customRangeIsValid"
      class="text-danger"
    >
      {{ $t('grid.widgets_section.walmart_people_flow.invalid_custom_range') }}
    </small>

    <div
      v-if="viewMode === 'current'"
      class="walmart-people-flow__summary"
      :class="{ 'walmart-people-flow__summary--critical': thresholdReached }"
    >
      <div>
        <small>{{ $t('grid.widgets_section.walmart_people_flow.current_occupancy') }}</small>
        <div class="walmart-people-flow__occupancy">{{ currentOccupancy }}</div>
      </div>
      <div class="text-right">
        <span class="badge" :class="thresholdReached ? 'badge-light-danger' : 'badge-light-success'">
          {{ thresholdReached ? $t('grid.widgets_section.walmart_people_flow.threshold_reached') : $t('grid.widgets_section.walmart_people_flow.normal') }}
        </span>
        <small class="d-block mt-50">
          {{ $t('grid.widgets_section.walmart_people_flow.threshold', { threshold }) }}
        </small>
      </div>
    </div>

    <div class="walmart-people-flow__chart">
      <canvas ref="chartCanvas" role="img" :aria-label="$t('grid.widgets_section.walmart_people_flow.chart_label')"></canvas>
      <div v-if="loading && !hasData" class="walmart-people-flow__state">
        <b-spinner small variant="primary" />
        <span>{{ $t('actions.loading') }}</span>
      </div>
      <div v-else-if="error && !hasData" class="walmart-people-flow__state text-danger">
        {{ error }}
      </div>
    </div>

    <small v-if="lastUpdated" class="walmart-people-flow__updated text-muted">
      {{ $t('grid.widgets_section.walmart_people_flow.updated_at', { time: lastUpdated }) }}
    </small>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue';
import Chart from 'chart.js/auto';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import axios from '@axios';
import useAppConfig from '@core/app-config/useAppConfig';

const PEOPLE_FLOW_ENDPOINT = '/v1/services/detection-events/walmart/people-flow';
const REFRESH_INTERVAL = 20 * 1000;
const MAX_HISTORICAL_RANGE_HOURS = 48;

export default {
  components: {
    BButton,
    BSpinner,
    flatPickr,
    vSelect,
  },
  setup() {
    const { skin } = useAppConfig();

    return { skin };
  },
  data() {
    return {
      chartInstance: null,
      chartPeopleFlow: null,
      chartRequestInProgress: false,
      currentSummary: null,
      customDateConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        minuteIncrement: 15,
        time_24hr: true,
      },
      customFrom: '',
      customTo: '',
      error: '',
      historicalRange: '12',
      historicalZoomInterval: 15,
      lastUpdated: '',
      liveZoomInterval: 15,
      loading: true,
      refreshTimer: null,
      summaryRequestInProgress: false,
      viewMode: 'current',
    };
  },
  computed: {
    currentOccupancy() {
      return this.currentSummary?.currentOccupancy ?? 0;
    },
    threshold() {
      return this.currentSummary?.threshold ?? 150;
    },
    chartRangeHours() {
      return this.currentSummary?.chartRangeHours ?? 2;
    },
    chartTimeZone() {
      return this.chartPeopleFlow?.timeZone ?? this.currentSummary?.timeZone;
    },
    thresholdReached() {
      return this.currentSummary?.thresholdReached ?? false;
    },
    hasData() {
      return Array.isArray(this.chartPeopleFlow?.series);
    },
    isDark() {
      return this.skin === 'dark';
    },
    viewModeOptions() {
      return [
        {
          label: this.$t('grid.widgets_section.walmart_people_flow.current_view'),
          value: 'current',
        },
        {
          label: this.$t('grid.widgets_section.walmart_people_flow.historical_view'),
          value: 'history',
        },
      ];
    },
    historicalRangeOptions() {
      return [12, 24, 48].map((hours) => ({
        label: this.$t('grid.widgets_section.walmart_people_flow.last_hours', { hours }),
        value: String(hours),
      })).concat({
        label: this.$t('grid.widgets_section.walmart_people_flow.custom_range'),
        value: 'custom',
      });
    },
    zoomIntervals() {
      return [60, 45, 30, 15, 5];
    },
    zoomInterval() {
      return this.viewMode === 'current' ? this.liveZoomInterval : this.historicalZoomInterval;
    },
    zoomIntervalIndex() {
      return this.zoomIntervals.indexOf(this.zoomInterval);
    },
    canZoomOut() {
      return this.zoomIntervalIndex > 0;
    },
    canZoomIn() {
      return this.zoomIntervalIndex >= 0
        && this.zoomIntervalIndex < this.zoomIntervals.length - 1;
    },
    chartSubtitle() {
      if (this.viewMode === 'current') {
        return this.$t('grid.widgets_section.walmart_people_flow.range_with_interval', {
          hours: this.chartRangeHours,
          minutes: this.liveZoomInterval,
        });
      }

      if (this.historicalRange === 'custom') {
        return this.$t('grid.widgets_section.walmart_people_flow.custom_range');
      }

      return this.$t('grid.widgets_section.walmart_people_flow.range_with_interval', {
        hours: this.historicalRange,
        minutes: this.historicalZoomInterval,
      });
    },
    customRangeIsValid() {
      const from = this.toIsoString(this.customFrom);
      const to = this.toIsoString(this.customTo);

      if (!from || !to) return false;

      const duration = new Date(to).getTime() - new Date(from).getTime();

      return duration > 0
        && duration <= MAX_HISTORICAL_RANGE_HOURS * 60 * 60 * 1000
        && new Date(to).getTime() <= Date.now();
    },
  },
  watch: {
    isDark() {
      this.updateChart();
    },
  },
  mounted() {
    this.fetchCurrentChart();
    this.refreshTimer = window.setInterval(this.refreshLiveData, REFRESH_INTERVAL);
  },
  beforeDestroy() {
    window.clearInterval(this.refreshTimer);
    this.chartInstance?.destroy();
  },
  methods: {
    getChartOptions() {
      const textColor = this.isDark ? '#D0D2D6' : '#5E5873';
      const gridColor = this.isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(94, 88, 115, 0.10)';

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: textColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
          },
          y: {
            beginAtZero: true,
            grid: { color: gridColor },
            ticks: {
              color: textColor,
              precision: 0,
              stepSize: 1,
            },
          },
        },
        plugins: {
          legend: {
            position: 'top',
            align: 'start',
            labels: {
              color: textColor,
              usePointStyle: true,
              boxWidth: 8,
            },
          },
          tooltip: {
            callbacks: {
              label(context) {
                return ` ${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
      };
    },
    getChartData() {
      const series = this.chartPeopleFlow?.series ?? [];

      return {
        labels: series.map(({ timestamp }) => this.formatChartLabel(timestamp)),
        datasets: [
          {
            label: this.$t('grid.widgets_section.walmart_people_flow.entering'),
            data: series.map(({ entering }) => entering),
            backgroundColor: '#28C76F',
            borderRadius: 4,
          },
          {
            label: this.$t('grid.widgets_section.walmart_people_flow.exiting'),
            data: series.map(({ exiting }) => exiting),
            backgroundColor: '#EA5455',
            borderRadius: 4,
          },
        ],
      };
    },
    formatChartLabel(timestamp) {
      const options = this.viewMode === 'history'
        ? {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }
        : {
          hour: '2-digit',
          minute: '2-digit',
        };

      if (this.chartTimeZone) options.timeZone = this.chartTimeZone;

      return new Intl.DateTimeFormat(undefined, options).format(new Date(timestamp));
    },
    updateChart() {
      if (!this.$refs.chartCanvas || !this.chartPeopleFlow) return;

      const chartData = this.getChartData();

      if (!this.chartInstance) {
        this.chartInstance = new Chart(this.$refs.chartCanvas, {
          type: 'bar',
          data: chartData,
          options: this.getChartOptions(),
        });
        return;
      }

      this.chartInstance.data = chartData;
      this.chartInstance.options = this.getChartOptions();
      this.chartInstance.update();
    },
    async fetchCurrentChart() {
      if (this.chartRequestInProgress) return;

      this.chartRequestInProgress = true;
      this.loading = true;
      this.error = '';

      try {
        const peopleFlow = await this.requestPeopleFlow({
          interval_minutes: this.liveZoomInterval,
        });
        this.currentSummary = peopleFlow;
        this.chartPeopleFlow = peopleFlow;
        this.setLastUpdated();
        this.$nextTick(() => this.updateChart());
      } catch (requestError) {
        this.error = this.errorMessage(requestError);
      } finally {
        this.loading = false;
        this.chartRequestInProgress = false;
      }
    },
    async fetchHistoricalChart() {
      if (this.chartRequestInProgress) return;

      if (this.historicalRange === 'custom' && !this.customRangeIsValid) {
        this.error = this.$t('grid.widgets_section.walmart_people_flow.invalid_custom_range');
        return;
      }

      this.chartRequestInProgress = true;
      this.loading = true;
      this.error = '';

      try {
        const peopleFlow = await this.requestPeopleFlow(this.historicalParams());
        this.currentSummary = peopleFlow;
        this.chartPeopleFlow = peopleFlow;
        this.setLastUpdated();
        this.$nextTick(() => this.updateChart());
      } catch (requestError) {
        this.error = this.errorMessage(requestError);
      } finally {
        this.loading = false;
        this.chartRequestInProgress = false;
      }
    },
    async fetchCurrentSummary() {
      if (this.summaryRequestInProgress) return;

      this.summaryRequestInProgress = true;

      try {
        this.currentSummary = await this.requestPeopleFlow({ summary_only: 1 });
        this.setLastUpdated();
      } catch (requestError) {
        this.error = this.errorMessage(requestError);
      } finally {
        this.summaryRequestInProgress = false;
      }
    },
    async requestPeopleFlow(params = {}) {
      const { data: response } = await axios.get(PEOPLE_FLOW_ENDPOINT, { params });

      return response?.data ?? response;
    },
    refreshLiveData() {
      if (this.viewMode === 'current') {
        this.fetchCurrentChart();
        return;
      }

      this.fetchCurrentSummary();
    },
    refreshSelectedView() {
      if (this.viewMode === 'current') {
        this.fetchCurrentChart();
        return;
      }

      this.fetchHistoricalChart();
    },
    onViewModeChange() {
      this.error = '';

      if (this.viewMode === 'current') {
        this.fetchCurrentChart();
        return;
      }

      this.fetchCurrentSummary();
      this.fetchHistoricalChart();
    },
    onHistoricalRangeChange() {
      this.error = '';

      if (this.historicalRange === 'custom') {
        this.setDefaultCustomRange();
        return;
      }

      this.fetchHistoricalChart();
    },
    zoomOut() {
      this.changeZoomLevel(-1);
    },
    zoomIn() {
      this.changeZoomLevel(1);
    },
    changeZoomLevel(direction) {
      const nextInterval = this.zoomIntervals[this.zoomIntervalIndex + direction];

      if (!nextInterval) return;

      if (this.viewMode === 'current') {
        this.liveZoomInterval = nextInterval;
        this.fetchCurrentChart();
        return;
      }

      this.historicalZoomInterval = nextInterval;
      this.fetchHistoricalChart();
    },
    applyCustomRange() {
      this.fetchHistoricalChart();
    },
    historicalParams() {
      if (this.historicalRange !== 'custom') {
        return {
          range_hours: Number(this.historicalRange),
          interval_minutes: this.historicalZoomInterval,
        };
      }

      return {
        from: this.toIsoString(this.customFrom),
        to: this.toIsoString(this.customTo),
        interval_minutes: this.historicalZoomInterval,
      };
    },
    setDefaultCustomRange() {
      const to = new Date();
      to.setSeconds(0, 0);
      to.setMinutes(Math.floor(to.getMinutes() / 15) * 15);
      const from = new Date(to.getTime() - 2 * 60 * 60 * 1000);

      this.customFrom = this.formatDateTime(from);
      this.customTo = this.formatDateTime(to);
    },
    formatDateTime(date) {
      const pad = (value) => String(value).padStart(2, '0');

      return date.getFullYear() + '-'
        + pad(date.getMonth() + 1) + '-'
        + pad(date.getDate()) + ' '
        + pad(date.getHours()) + ':'
        + pad(date.getMinutes());
    },
    toIsoString(value) {
      if (!value) return null;

      const date = new Date(value.replace(' ', 'T'));

      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    },
    setLastUpdated() {
      this.lastUpdated = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    },
    errorMessage(requestError) {
      return requestError?.response?.data?.message
        ?? this.$t('grid.widgets_section.walmart_people_flow.load_error');
    },
  },
};
</script>

<style lang="scss" scoped>
.walmart-people-flow {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 220px;
  padding: 0.75rem;
}

.walmart-people-flow__header,
.walmart-people-flow__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.walmart-people-flow__filters,
.walmart-people-flow__custom-range {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.walmart-people-flow__zoom,
.walmart-people-flow__zoom-actions {
  display: flex;
  align-items: center;
}

.walmart-people-flow__zoom {
  justify-content: space-between;
  margin-top: 0.5rem;
}

.walmart-people-flow__zoom-actions {
  gap: 0.25rem;
}

.walmart-people-flow__filters > * {
  flex: 1;
  min-width: 0;
}

.walmart-people-flow__custom-range .form-control {
  min-width: 0;
}

.walmart-people-flow__summary {
  margin: 0.75rem 0;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(40, 199, 111, 0.3);
  border-radius: 0.5rem;
}

.walmart-people-flow__summary--critical {
  border-color: rgba(234, 84, 85, 0.7);
  background-color: rgba(234, 84, 85, 0.08);
}

.walmart-people-flow__occupancy {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
}

.walmart-people-flow__chart {
  position: relative;
  flex: 1;
  min-height: 0;
}

.walmart-people-flow__state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-align: center;
}

.walmart-people-flow__updated {
  margin-top: 0.5rem;
  text-align: right;
}

.walmart-people-flow__refreshing {
  animation: walmart-people-flow-spin 1s linear infinite;
}

@keyframes walmart-people-flow-spin {
  to { transform: rotate(360deg); }
}
</style>
